import { PixelRatio } from 'react-native';
import type { i18n } from 'i18next';
import { getIpAddress, getDeviceLanguage, isPlatformWeb } from '@24i/nxg-core-utils';
import { RuntimeConfig } from '@24i/nxg-core-utils/src/globalSingletons';
import { Dimensions, Platform } from '@24i/nxg-sdk-quantum';
import { AppSettings } from '@24i/nxg-sdk-photon';
import { getAdvertisingInfo } from './advertisingInfo';
import { getDeviceInfo } from '../../analytics/utils';
import { getPseudoId as getUserPseudoId } from '../../context/UserData';
import { AdvertisingInfo } from './advertisingInfo/common';
import { DeviceInfo } from '../../analytics/types';

export interface AdvertisementParameters {
    platform?: string;
    dataPrivacy?: {
        regulation?: 'gdpr' | 'ccpa' | '';
        consent?: string | '';
        lat?: 0 | 1 | null;
        dnt?: 0 | 1 | null;
        gppString?: string | '';
        gppSid?: string | '';
    };
    device?: {
        id?: string;
        idType?: 'idfa' | 'gaid' | 'sessionid';
        ip?: string;
        maker?: string;
        model?: string;
        os?: string;
        osVersion?: string;
        language?: string;
    };
    user?: {
        pseudoId?: string;
        sessionId?: string;
    };
    app?: {
        language?: string;
        bundle?: string;
        name?: string;
        storeUrl?: string;
    };
    player?: {
        playbackId?: string;
        width?: number;
        height?: number;
        muted?: 0 | 1;
    };
    media?: {
        language?: string;
        adPodMaxDur?: number | null;
    };
}

let memoizedIpAddress: Promise<string>;
export const getMemoizedIpAddress = (): Promise<string> => {
    if (!memoizedIpAddress) {
        memoizedIpAddress = getIpAddress();
    }
    return memoizedIpAddress;
};

export const getStoreUrl = (): string => {
    const appBundle = RuntimeConfig.get().appStoreUrl;
    if (appBundle) return appBundle;
    if (Platform.OS === 'web') {
        // we don't really know the base URL (especially production one)
        const url = new URL(window.location.href);
        url.pathname = '';
        url.search = '';
        url.hash = '';
        return url.toString();
    }
    return '';
};

interface AdvertisementParametersDependencies {
    privacyConfig: AppSettings['features']['data_privacy'];
    adInsertionConfig: AppSettings['features']['adInsertion'];
    i18n: i18n;
    sessionId: string;
    playbackId: string;
}

interface AdvertisementParametersDependenciesSync extends AdvertisementParametersDependencies {
    advertisingInfo: AdvertisingInfo;
    deviceInfo: DeviceInfo;
    pseudoId: string;
}

const unknownToEmptyString = (value: string): string => {
    if (value === 'unknown') {
        return '';
    }
    return value;
};

const assembleAdvertisementParameters = ({
    privacyConfig,
    adInsertionConfig,
    advertisingInfo,
    deviceInfo,
    pseudoId,
    sessionId,
    playbackId,
    i18n,
}: AdvertisementParametersDependenciesSync): AdvertisementParameters | undefined => {
    const dataPrivacy: AdvertisementParameters['dataPrivacy'] = {
        regulation: '',
        consent: '',
        dnt: null,
        lat: null,
        gppString: '',
        gppSid: '',
    };
    if (privacyConfig?.gdpr) {
        dataPrivacy.regulation = 'gdpr';
        dataPrivacy.consent = advertisingInfo.tcfConsent || '';
        dataPrivacy.gppString = advertisingInfo.gppString || '';
        dataPrivacy.gppSid = advertisingInfo.gppSid || '';
    }

    if (advertisingInfo.isAdTrackingLimited !== null) {
        dataPrivacy.lat = advertisingInfo.isAdTrackingLimited ? 1 : 0;
    }

    if (advertisingInfo.doNotTrack !== null) {
        dataPrivacy.dnt = advertisingInfo.doNotTrack ? 1 : 0;
    }

    const runtimeConfig = RuntimeConfig.get();

    const { height: windowHeight, width: windowWidth } = Dimensions.get('window');
    const pixelRatio = PixelRatio.get();
    const [deviceLanguage] = getDeviceLanguage()?.split(/[-_]/);

    return {
        platform: deviceInfo.platform,
        dataPrivacy,
        device: {
            id: advertisingInfo.advertisingId,
            idType: Platform.select({
                ios: 'idfa',
                android: 'gaid',
                web: 'sessionid',
                default: 'sessionid',
            }),
            maker: unknownToEmptyString(deviceInfo.deviceManufacturer),
            model: unknownToEmptyString(deviceInfo.deviceModel),
            os: deviceInfo.platform,
            osVersion: isPlatformWeb ? '' : `${deviceInfo.osVersion}`,
            language: deviceLanguage,
        },
        user: {
            pseudoId: pseudoId || '',
            sessionId: sessionId || '',
        },
        app: {
            name: runtimeConfig.appName,
            bundle: runtimeConfig.appBundle,
            storeUrl: getStoreUrl(),
            language: i18n.language,
        },
        player: {
            width: windowWidth * pixelRatio,
            height: windowHeight * pixelRatio,
            muted: 0,
            playbackId,
        },
        media: {
            language: i18n.language,
            adPodMaxDur: adInsertionConfig?.adPodMaxDur || null,
        },
    };
};

export const buildAdvertisementParameters = async ({
    privacyConfig,
    adInsertionConfig,
    i18n,
    sessionId,
    playbackId,
}: AdvertisementParametersDependencies): Promise<AdvertisementParameters | undefined> => {
    const advertisingInfo = await getAdvertisingInfo();
    const deviceInfo = await getDeviceInfo();
    const pseudoId = await getUserPseudoId();
    return assembleAdvertisementParameters({
        privacyConfig,
        adInsertionConfig,
        i18n,
        sessionId,
        playbackId,
        advertisingInfo,
        deviceInfo,
        pseudoId,
    });
};
